import React, { ReactNode, useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "@/components/ds/icons";

type SubLink = {
  label: string;
  to: string;
  end?: boolean;
};

type ExpandableMenuLinkProps = {
  label: string;
  icon: ReactNode;
  onExpand: () => void;
  expanded: boolean;
  children: ReactNode;
  toggleSidebar: boolean;
  to?: string;
};

export default function ExpandableMenuLink({
  label,
  icon,
  onExpand,
  expanded,
  children,
  toggleSidebar,
  to
}: ExpandableMenuLinkProps) {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const linkRef = useRef<HTMLDivElement | null>(null);

  const linkStyling = "h-9 mb-1 mt-1 rounded-lg w-full text-left flex items-center";

  const subLinks: SubLink[] =
    (React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.props) {
        const { label, to, end } = child.props as { label: string; to: string; end?: boolean };
        return { label, to, end };
      }
      return null;
    })?.filter(Boolean) as SubLink[]) || [];

  const handleEnter = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    setIsHovered(true);
  };

  const handleLeave = () => {
    hideTimeout.current = setTimeout(() => setIsHovered(false), 150);
  };

  useEffect(() => {
    return () => { if (hideTimeout.current) clearTimeout(hideTimeout.current); };
  }, []);

  useEffect(() => {
    if (isHovered && !toggleSidebar && linkRef.current) {
      const linkRect = linkRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const tipHeight = tooltipRef.current?.offsetHeight ?? 120;
      let top = linkRect.top + linkRect.height / 2 - tipHeight / 2;
      if (top < 10) top = 10;
      if (top + tipHeight > viewportHeight - 10) top = viewportHeight - tipHeight - 10;
      setTooltipPosition({ top });
    }
  }, [isHovered, toggleSidebar]);

  const isActive = to
    ? location.pathname.startsWith(to)
    : subLinks.some((subLink) => location.pathname.startsWith(subLink.to));

  return (
    <>
      <div ref={linkRef} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
        <div
          className={`${isActive ? "v-nav-active" : "v-nav-hover"} ${linkStyling}`}
          onClick={onExpand}
        >
          <div className="flex w-full py-2">
            <div className="flex items-center ml-2">{icon}</div>
            <div className="flex items-center pl-4 w-full text-sm">
              {label}
              {toggleSidebar && (
                <div className="flex ml-auto pr-4">
                  <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isHovered && !toggleSidebar && (
        <div
          ref={tooltipRef}
          className="fixed left-full ml-1 p-2 rounded v-nav whitespace-nowrap z-[1000]"
          style={{ top: tooltipPosition.top }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          {subLinks.map((subLink) => (
            <NavLink
              end={subLink.end ?? true}
              key={subLink.to}
              to={subLink.to}
              className={({ isActive }) =>
                `${isActive ? "v-nav-active" : ""} block px-2 py-2 v-nav-hover rounded`
              }
            >
              {subLink.label}
            </NavLink>
          ))}
        </div>
      )}

      {toggleSidebar && expanded && (
        <div className="flex flex-col flex-none">{children}</div>
      )}
    </>
  );
}
